import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";

/**
 * Det redaktoeren legger igjen paa en loggpost.
 *
 * TRE TING, SAMME PLASS: en kommentar, en markering, og et synkspoersmaal
 * skrevet ut av posten. De hoerer til posten og vises paa den — men de er
 * ikke loggen, og de ligger i egne tabeller baade her og i Postgres.
 *
 * EN MERKNAD KAN ALDRI KREVE NOE AV REGI. Ingen tilstand aa gjoere opp,
 * ingenting som kan hindre at dagen avsluttes, ingen hake som maa settes.
 * Den staar der, og regi gjoer med den hva hen vil. Markeringen er det
 * samme: redaktoeren har festet seg ved posten, og det er alt den sier.
 *
 * OFFLINE FOERST, som dokumentene og synkspoersmaalene. Det som er hentet
 * ligger lokalt og vises uten nett.
 *
 * ÉN VEI NED. Regi svarer ikke herfra — svaret er at hen logger videre.
 */

export type Slag = "kommentar" | "markering" | "spoersmaal";

export interface Merknad {
  readonly id: string;
  readonly entryId: string;
  readonly slag: Slag;
  readonly tekst: string;
  readonly forfatter: string;
}

export async function lesLokalt(produksjonId: string): Promise<Merknad[]> {
  await db.openDb(produksjonId);
  return (await db.lastMerknader(produksjonId)) as Merknad[];
}

/**
 * Henter alt som henger paa produksjonens poster, og skriver over det lokale.
 *
 * Spoerringene gaar paa PRODUKSJON og ikke paa oekt: en logg som ble skrevet
 * i gaar kan faa en kommentar i dag, og regi skal se den der posten staar.
 */
export async function synkroniser(produksjonId: string): Promise<Merknad[] | null> {
  if (supabase === null || !erEkteProduksjon(produksjonId)) return null;

  const [kommentarer, markeringer, spoersmaal] = await Promise.all([
    supabase
      .from("entry_comment")
      .select("id, text, created_at, log_entry!inner(id, production_id),"
        + " production_member(display_name)")
      .eq("log_entry.production_id", produksjonId)
      .order("created_at", { ascending: true }),
    supabase
      .from("entry_mark")
      .select("log_entry_id, created_at, log_entry!inner(production_id),"
        + " production_member(display_name)")
      .eq("log_entry.production_id", produksjonId),
    supabase
      .from("su_question")
      .select("id, text, source_entry_id, participant(abbreviation)")
      .eq("production_id", produksjonId)
      .not("source_entry_id", "is", null),
  ]);

  const feil = kommentarer.error ?? markeringer.error ?? spoersmaal.error;
  if (feil !== null && feil !== undefined) {
    /* Samme moenster som `skriv` i SessionProvider: en henting som feiler i
       stillhet er det verste utfallet — regi ville staatt uten beskjeden
       uten at noen visste det. */
    // eslint-disable-next-line no-console
    console.error("[infoflyt] tilbakemelding: henting feilet", feil);
    return null;
  }

  const ut: Merknad[] = [];

  for (const r of kommentarer.data ?? []) {
    const rad = r as unknown as Record<string, unknown>;
    const post = rad["log_entry"] as Record<string, unknown> | null;
    const medlem = rad["production_member"] as Record<string, unknown> | null;
    if (post === null) continue;
    ut.push({
      id: String(rad["id"]),
      entryId: String(post["id"]),
      slag: "kommentar",
      tekst: String(rad["text"] ?? ""),
      forfatter: String(medlem?.["display_name"] ?? ""),
    });
  }

  for (const r of markeringer.data ?? []) {
    const rad = r as unknown as Record<string, unknown>;
    const medlem = rad["production_member"] as Record<string, unknown> | null;
    const postId = String(rad["log_entry_id"]);
    ut.push({
      /* Markeringen har ingen egen id i Postgres — noekkelen er (post,
         forfatter). Her trengs én, og den settes sammen av de to. */
      id: `markering:${postId}`,
      entryId: postId,
      slag: "markering",
      tekst: "",
      forfatter: String(medlem?.["display_name"] ?? ""),
    });
  }

  for (const r of spoersmaal.data ?? []) {
    const rad = r as unknown as Record<string, unknown>;
    const deltaker = rad["participant"] as Record<string, unknown> | null;
    ut.push({
      id: String(rad["id"]),
      entryId: String(rad["source_entry_id"]),
      slag: "spoersmaal",
      tekst: String(rad["text"] ?? ""),
      forfatter: String(deltaker?.["abbreviation"] ?? "ALLE"),
    });
  }

  await db.openDb(produksjonId);
  try {
    await db.erstattMerknader(produksjonId, ut);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[infoflyt] tilbakemelding: lagring feilet", e);
    /* Lagringen feilet, men det som ble hentet er gyldig. Regi skal se
       beskjeden naa; den hentes paa nytt neste gang uansett. */
  }
  return ut;
}
